import React, { useState } from 'react';
import { HazardAlert } from '../types';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface HazardDetailModalProps {
  hazard: HazardAlert;
  isOpen: boolean;
  onClose: () => void;
}

const HazardDetailModal: React.FC<HazardDetailModalProps> = ({ hazard, isOpen, onClose }) => {
  if (!isOpen) return null;

  const getHazardIcon = (type: string) => {
    switch (type) {
      case 'roadwork': return '🔨';
      case 'accident': return '🚗';
      case 'flooding': return '💧';
      case 'debris': return '⚠️';
      case 'closure': return '🚫';
      case 'construction': return '🚧';
      default: return '⚠️';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low': return 'bg-yellow-500 text-white';
      case 'medium': return 'bg-orange-500 text-white';
      case 'high': return 'bg-red-500 text-white';
      case 'urgent': return 'bg-purple-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getHazardTypeColor = (type: string) => {
    switch (type) {
      case 'roadwork': return 'bg-orange-100 text-orange-800';
      case 'accident': return 'bg-red-100 text-red-800';
      case 'flooding': return 'bg-blue-100 text-blue-800';
      case 'debris': return 'bg-yellow-100 text-yellow-800';
      case 'closure': return 'bg-purple-100 text-purple-800';
      case 'construction': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Create custom icon for map marker
  const createCustomIcon = () => {
    return L.divIcon({
      html: `
        <div style="
          background-color: ${hazard.severity === 'urgent' ? '#A855F7' : 
                            hazard.severity === 'high' ? '#EF4444' : 
                            hazard.severity === 'medium' ? '#FB923C' : '#FCD34D'};
          color: white;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        ">
          ${getHazardIcon(hazard.hazardType)}
        </div>
      `,
      className: 'custom-hazard-marker',
      iconSize: [30, 30],
      iconAnchor: [15, 15]
    });
  };

  const openInMaps = () => {
    const lat = hazard.coordinate.latitude;
    const lng = hazard.coordinate.longitude;
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Close button */}
        <div className="flex justify-end p-4">
          <button
            onClick={onClose}
            className="text-blue-600 font-semibold text-lg hover:text-blue-800 transition-colors"
          >
            Done
          </button>
        </div>

        {/* Single Card Content */}
        <div className="px-4 pb-4">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
            {/* Header with severity badge */}
            <div className="p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-1">
                  {/* Severity Badge */}
                  <div className="inline-flex items-center space-x-2 mb-4">
                    <span className="text-white">{getHazardIcon(hazard.hazardType)}</span>
                    <span className={`text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full ${getSeverityColor(hazard.severity)}`}>
                      {hazard.severity}
                    </span>
                  </div>
                  
                  {/* Title and Type */}
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{hazard.title}</h2>
                  <p className="text-sm text-gray-500 uppercase tracking-wide">
                    {hazard.hazardType.replace('_', ' ').charAt(0).toUpperCase() + hazard.hazardType.replace('_', ' ').slice(1)}
                  </p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="px-6 pb-4">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Description</h3>
              <p className="text-gray-700 leading-relaxed">{hazard.description}</p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-500">
                <span>Reported: {formatDate(hazard.createdAt)}</span>
              </div>
            </div>

            {/* Mini Map */}
            <div className="px-6 pb-4">
              <div className="relative">
                <div className="h-48 rounded-lg overflow-hidden border border-gray-200">
                  <MapContainer
                    center={[hazard.coordinate.latitude, hazard.coordinate.longitude]}
                    zoom={15}
                    style={{ height: '100%', width: '100%' }}
                    zoomControl={true}
                    scrollWheelZoom={true}
                    dragging={true}
                    doubleClickZoom={true}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    <Marker
                      position={[hazard.coordinate.latitude, hazard.coordinate.longitude]}
                      icon={createCustomIcon()}
                    >
                      <Popup>
                        <div className="text-center p-2">
                          <h3 className="font-semibold text-sm">{hazard.title}</h3>
                          <p className="text-xs text-gray-600">{hazard.description}</p>
                        </div>
                      </Popup>
                    </Marker>
                  </MapContainer>
                </div>
                
                {/* Hazard Type Overlay Badge */}
                <div className="absolute top-2 right-2">
                  <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-semibold text-white ${getSeverityColor(hazard.severity)}`}>
                    <span>{getHazardIcon(hazard.hazardType)}</span>
                    <span>{hazard.hazardType.replace('_', ' ').charAt(0).toUpperCase() + hazard.hazardType.replace('_', ' ').slice(1)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Location Info */}
            <div className="px-6 pb-4">
              <h3 className="text-lg font-semibold text-blue-600 mb-3">Location</h3>
              <div className="space-y-2">
                <p className="text-gray-700 font-medium">{hazard.island}</p>
                <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                  <span>500m radius</span>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div className="px-6 pb-6">
              <button
                onClick={openInMaps}
                className="w-full bg-blue-600 text-white font-semibold py-4 px-6 rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                <span>Open in Maps</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HazardDetailModal;
